import {
  SYNC_PUSH_MAX,
  newId,
  type SyncApplyResult,
  type SyncEntity,
  type SyncMutation,
} from "@haulsheet/core";
import { apiFetch, ApiClientError, isServerUnreachable } from "../api/client";
import {
  ENTITY_TABLES,
  entityTable,
  getDb,
  type EntityTable,
  type HaulSheetDB,
} from "../db/dexie";
import type { DeadLetterRow, OutboxRow } from "../db/types";
import { useSyncStore } from "../store/syncStore";

type Listener = () => void;

interface PushResultItem {
  mutationId: string;
  result: SyncApplyResult;
  row?: Record<string, unknown> & { id: string; version: number };
  reason?: string;
}

interface PushResponse {
  results: PushResultItem[];
}

interface PullRecord {
  entity: SyncEntity;
  op: "upsert" | "delete";
  id: string;
  version: number;
  row?: Record<string, unknown>;
}

interface PullResponse {
  changes: PullRecord[];
  cursor: string;
  hasMore: boolean;
}

const BACKOFF_MIN_MS = 2000;
const BACKOFF_MAX_MS = 5 * 60 * 1000;

/**
 * Server rows must carry numeric versions: a string version stored locally is
 * sent back as baseVersion "1", which the push schema rejects for the whole batch.
 */
export function normalizeServerRow<T extends Record<string, unknown>>(row: T): T {
  if (typeof row["version"] === "string") {
    const n = Number(row["version"]);
    if (Number.isFinite(n)) return { ...row, version: n };
  }
  return row;
}

function isEntityTable(entity: string): entity is EntityTable {
  return (ENTITY_TABLES as readonly string[]).includes(entity);
}

export class SyncClient {
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private intervalTimer: ReturnType<typeof setInterval> | null = null;
  private backoffMs = BACKOFF_MIN_MS;
  private syncing = false;
  private lastStamp = 0;
  /** A sync was requested while one was running; run again when it ends. */
  private rerun = false;
  /** Outbox mutationIds currently on the wire. Coalescing must not touch them. */
  private inflight = new Set<string>();
  private started = false;
  private listeners = new Set<Listener>();

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.syncNow();
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("online", this.onOnline);
    this.intervalTimer = setInterval(() => {
      if (document.visibilityState === "visible") void this.syncNow();
    }, 60_000);
  }

  stop(): void {
    this.started = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("online", this.onOnline);
    if (this.pushTimer) clearTimeout(this.pushTimer);
    if (this.intervalTimer) clearInterval(this.intervalTimer);
    this.pushTimer = null;
    this.intervalTimer = null;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private onVisibility = (): void => {
    if (document.visibilityState === "visible") void this.syncNow();
  };

  private onOnline = (): void => {
    this.backoffMs = BACKOFF_MIN_MS;
    void this.syncNow();
  };

  private schedulePush(): void {
    // Mid-sync edit: push it as soon as the current sync ends.
    if (this.syncing) this.rerun = true;
    if (this.pushTimer) clearTimeout(this.pushTimer);
    this.pushTimer = setTimeout(() => void this.syncNow(), 2000);
  }

  async pendingCount(): Promise<number> {
    return getDb().outbox.count();
  }

  /**
   * Write Dexie row + outbox in one transaction.
   * Coalesces unsent mutations for the same entity+id.
   */
  async mutate(args: {
    entity: SyncEntity;
    op: "upsert" | "delete";
    id: string;
    baseVersion: number | null;
    patch?: Record<string, unknown>;
    row?: Record<string, unknown>;
  }): Promise<void> {
    if (!isEntityTable(args.entity)) {
      throw new Error(`Unknown sync entity: ${args.entity}`);
    }
    const db = getDb();
    // Strictly increasing per client: the outbox pushes in createdAt order, and
    // a broker create must reach the server before the load that points at it
    // (FK), even when both are written in the same millisecond.
    const stamp = Math.max(Date.now(), this.lastStamp + 1);
    this.lastStamp = stamp;
    const clientChangedAt = new Date(stamp).toISOString();
    const mutationId = newId();

    await db.transaction(
      "rw",
      [entityTable(db, args.entity), db.outbox, db.deadLetter],
      async () => {
        const table = entityTable(db, args.entity);
        if (args.op === "delete") {
          const existing = await table.get(args.id);
          if (existing) {
            await table.put({
              ...existing,
              deletedAt: clientChangedAt,
              updatedAt: clientChangedAt,
            });
          }
        } else if (args.row) {
          await table.put(args.row as never);
        } else if (args.patch) {
          const existing = await table.get(args.id);
          if (existing) {
            await table.put({
              ...existing,
              ...args.patch,
              updatedAt: clientChangedAt,
            } as never);
          }
        }

        const all = await db.outbox
          .where({ entity: args.entity, id: args.id })
          .toArray();
        // Rows already sent can't be rewritten: the server may have applied
        // them. Re-sending a sent create would come back row_exists and drop
        // this edit into the dead letter. A follow-up behind an in-flight
        // create is rebased onto the server version when the create lands.
        const pending = all.filter((p) => !this.inflight.has(p.mutationId));
        const onWire = all.length - pending.length;

        // Status steps are never merged: the server checks each transition
        // from the one before it, so booked + rolling queued together must
        // go as two mutations, not one considering -> rolling.
        if (isSeparateStatusStep(args, pending)) {
          await db.outbox.put({
            mutationId,
            entity: args.entity,
            op: args.op,
            id: args.id,
            baseVersion: pending[0]!.baseVersion,
            patch: args.patch ?? null,
            clientChangedAt,
            createdAt: clientChangedAt,
          });
          return;
        }

        for (const p of pending) {
          await db.outbox.delete(p.mutationId);
        }

        // Deleting a row the server has never seen: nothing to send. Pushing
        // the delete would be rejected not_found and land in the dead letter.
        if (
          args.op === "delete" &&
          onWire === 0 &&
          pending.some((p) => p.baseVersion == null && p.op === "upsert")
        ) {
          return;
        }

        // The server refused this record's create: anything sent now would be a
        // partial create and fail too. Park the change with the failed one so
        // Retry sends them together and Discard drops them together.
        const failedCreate = (
          await db.deadLetter.toArray()
        ).some((d) => d.entity === args.entity && d.id === args.id && d.op === "upsert" && d.baseVersion == null);
        if (failedCreate && onWire === 0) {
          const parked = mergePendingPatches(pending, args.patch ?? args.row ?? null);
          await db.deadLetter.put({
            mutationId,
            entity: args.entity,
            op: args.op,
            id: args.id,
            baseVersion: null,
            patch: args.op === "delete" ? null : parked,
            clientChangedAt,
            createdAt: clientChangedAt,
            reason: HELD_REASON,
            at: clientChangedAt,
          });
          return;
        }

        // Coalesce: if we deleted prior unsent patches, keep earliest baseVersion for create chain
        let baseVersion = args.baseVersion;
        if (pending.length > 0) {
          const creates = pending.filter((p) => p.baseVersion == null);
          if (creates.length > 0) baseVersion = null;
          else if (args.op === "upsert") {
            baseVersion = pending[0]!.baseVersion;
          }
        }

        // Coalesce patches too: the replaced unsent mutations may carry fields
        // this one doesn't (e.g. a create's full row, then a status-only edit).
        // Dropping them would push a create with only {status} and lose the load.
        const merged = mergePendingPatches(pending, args.patch ?? args.row ?? null);

        const outboxRow: OutboxRow = {
          mutationId,
          entity: args.entity,
          op: args.op,
          id: args.id,
          baseVersion,
          patch: args.op === "delete" ? null : merged,
          clientChangedAt,
          createdAt: clientChangedAt,
        };
        await db.outbox.put(outboxRow);
      },
    );

    const count = await this.pendingCount();
    useSyncStore.getState().setPendingCount(count);
    this.emit();
    this.schedulePush();
  }

  async syncNow(): Promise<void> {
    if (this.syncing) {
      // Don't strand an edit made mid-sync until the next 60 s tick.
      this.rerun = true;
      return;
    }
    this.rerun = false;
    if (!navigator.onLine) {
      useSyncStore.getState().setStatus("offline");
      return;
    }
    this.syncing = true;
    useSyncStore.getState().setStatus("syncing");
    try {
      await this.push();
      await this.pull();
      this.backoffMs = BACKOFF_MIN_MS;
      useSyncStore.getState().setStatus("idle");
      useSyncStore.getState().setLastError(null);
      useSyncStore.getState().setLastSyncAt(new Date().toISOString());
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      useSyncStore.getState().setStatus("error");
      useSyncStore.getState().setLastError(message);
      this.rerun = false; // backoff owns the retry
      this.scheduleBackoff();
    } finally {
      this.syncing = false;
      useSyncStore.getState().setPendingCount(await this.pendingCount());
      this.emit();
    }
    if (this.rerun && (await this.pendingCount()) > 0) {
      await this.syncNow();
    }
    this.rerun = false;
  }

  private scheduleBackoff(): void {
    if (this.pushTimer) clearTimeout(this.pushTimer);
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
    this.pushTimer = setTimeout(() => void this.syncNow(), delay);
  }

  private async push(): Promise<void> {
    const db = getDb();
    while (true) {
      const batch = await db.outbox.orderBy("createdAt").limit(SYNC_PUSH_MAX).toArray();
      if (batch.length === 0) break;
      for (const b of batch) this.inflight.add(b.mutationId);
      try {
        await this.pushBatch(db, batch);
      } finally {
        for (const b of batch) this.inflight.delete(b.mutationId);
      }
      if (batch.length < SYNC_PUSH_MAX) break;
    }
  }

  private async pushBatch(db: HaulSheetDB, batch: OutboxRow[]): Promise<void> {
    const mutations: SyncMutation[] = batch.map((row) => ({
      mutationId: row.mutationId,
      entity: row.entity,
      op: row.op,
      id: row.id,
      baseVersion: row.baseVersion == null ? null : Number(row.baseVersion),
      patch: row.patch ?? undefined,
      clientChangedAt: row.clientChangedAt,
    }));

    const res = await apiFetch<PushResponse>("/api/v1/sync/push", {
      method: "POST",
      body: JSON.stringify({ mutations }),
    });

    for (const item of res.results) {
      const local = batch.find((b) => b.mutationId === item.mutationId);
      if (!local) continue;

      if (
        item.result === "applied" ||
        item.result === "merged" ||
        item.result === "conflict_review"
      ) {
        await db.outbox.delete(item.mutationId);
        await this.landServerRow(db, local, item);
      } else if (item.result === "rejected") {
        const dead: DeadLetterRow = {
          ...local,
          reason: item.reason ?? "rejected",
          at: new Date().toISOString(),
          result: item.result,
        };
        await db.deadLetter.put(dead);
        await db.outbox.delete(item.mutationId);
      }
    }
  }

  /**
   * Write the server's row back. If the user edited this row while it was on
   * the wire, keep their local values (they're queued) and take only the
   * server version, and rebase queued follow-ups of a create onto it.
   */
  private async landServerRow(
    db: HaulSheetDB,
    sent: OutboxRow,
    item: PushResultItem,
  ): Promise<void> {
    if (!item.row || !isEntityTable(sent.entity)) return;
    const table = entityTable(db, sent.entity);
    const serverRow = normalizeServerRow(item.row);
    const version = serverRow.version;
    await db.transaction("rw", [table, db.outbox], async () => {
      const queued = await db.outbox
        .where({ entity: sent.entity, id: sent.id })
        .toArray();
      if (queued.length === 0) {
        await table.put(serverRow as never);
        return;
      }
      for (const q of queued) {
        if (q.baseVersion == null && !this.inflight.has(q.mutationId)) {
          await db.outbox.update(q.mutationId, { baseVersion: version });
        }
      }
      const local = await table.get(sent.id);
      await table.put((local ? { ...local, version } : serverRow) as never);
    });
  }

  /**
   * Re-send a record's rejected changes as one fresh mutation (new
   * mutationId: the server answers a repeated id with its first result).
   */
  async retryFailed(entity: SyncEntity, id: string): Promise<void> {
    if (!isEntityTable(entity)) return;
    const db = getDb();
    const failed = await this.failedFor(entity, id);
    if (failed.length === 0) return;
    const table = entityTable(db, entity);
    const local = (await table.get(id)) as Record<string, unknown> | undefined;
    const last = failed[failed.length - 1]!;
    const localVersion = Number(local?.["version"] ?? 0);
    const wasCreate =
      failed.some((f) => f.op === "upsert" && f.baseVersion == null) && !(localVersion > 0);
    // One transaction: the failed rows leave the dead letter exactly when the
    // retry enters the outbox (and mutate mustn't see them, or it would park it).
    await db.transaction("rw", [table, db.outbox, db.deadLetter], async () => {
      await db.deadLetter.bulkDelete(failed.map((f) => f.mutationId));
      if (last.op === "delete") {
        await this.mutate({ entity, op: "delete", id, baseVersion: localVersion || last.baseVersion });
      } else {
        const patch = mergePendingPatches(failed, null) ?? {};
        await this.mutate({
          entity,
          op: "upsert",
          id,
          baseVersion: wasCreate ? null : localVersion || last.baseVersion,
          patch,
          row: { ...(local ?? {}), ...patch, updatedAt: new Date().toISOString() },
        });
      }
    });
    useSyncStore.getState().setPendingCount(await this.pendingCount());
    this.emit();
    void this.syncNow();
  }

  /**
   * Drop a record's rejected changes and put the record back the way the
   * server has it. A create the server never accepted is removed (with any
   * edits queued on top of it). Edits/deletes need the network to fetch the
   * server copy: throws OfflineError before touching anything.
   */
  async discardFailed(entity: SyncEntity, id: string): Promise<void> {
    if (!isEntityTable(entity)) return;
    const db = getDb();
    const failed = await this.failedFor(entity, id);
    if (failed.length === 0) return;
    const table = entityTable(db, entity);
    const local = (await table.get(id)) as Record<string, unknown> | undefined;
    const neverOnServer =
      failed.some((f) => f.op === "upsert" && f.baseVersion == null) &&
      failed.every((f) => f.reason !== "row_exists") &&
      !(Number(local?.["version"] ?? 0) > 0);

    let serverRow: Record<string, unknown> | null = null;
    if (!neverOnServer) {
      if (!navigator.onLine) throw new OfflineError();
      try {
        const res = await apiFetch<{ row: Record<string, unknown> | null }>(
          `/api/v1/sync/row/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`,
        );
        serverRow = res.row ? normalizeServerRow(res.row) : null;
      } catch (err) {
        if (err instanceof ApiClientError && !isServerUnreachable(err)) throw err;
        throw new OfflineError();
      }
    }

    await db.transaction("rw", [table, db.outbox, db.deadLetter], async () => {
      const queued = (await db.outbox.where({ entity, id }).toArray()).filter(
        (q) => !this.inflight.has(q.mutationId),
      );
      if (serverRow == null) {
        // Nothing on the server to go back to: the record is gone here too.
        for (const q of queued) await db.outbox.delete(q.mutationId);
        await table.delete(id);
      } else {
        // Server copy, with any newer queued edits still layered on top.
        const still = mergePendingPatches(queued, null) ?? {};
        await table.put({ ...serverRow, ...still } as never);
      }
      await db.deadLetter.bulkDelete(failed.map((f) => f.mutationId));
    });
    useSyncStore.getState().setPendingCount(await this.pendingCount());
    this.emit();
  }

  private async failedFor(entity: SyncEntity, id: string): Promise<DeadLetterRow[]> {
    const all = await getDb().deadLetter.toArray();
    return all
      .filter((d) => d.entity === entity && d.id === id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  private async pull(): Promise<void> {
    const db = getDb();
    let cursor = (await db.meta.get("syncCursor"))?.value ?? "0";

    for (;;) {
      try {
        const res = await apiFetch<PullResponse>(
          `/api/v1/sync/pull?cursor=${encodeURIComponent(cursor)}&limit=500`,
        );

        await this.applyPull(db, res.changes);
        cursor = res.cursor;
        await db.meta.put({ key: "syncCursor", value: cursor });

        if (!res.hasMore) break;
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 410) {
          // Cursor expired: wipe local except outbox, pull from 0
          await this.wipeLocalKeepOutbox(db);
          cursor = "0";
          await db.meta.put({ key: "syncCursor", value: "0" });
          continue;
        }
        throw err;
      }
    }
  }

  private async applyPull(db: HaulSheetDB, changes: PullRecord[]): Promise<void> {
    for (const change of changes) {
      if (!isEntityTable(change.entity)) continue;
      const table = entityTable(db, change.entity);
      const pending = await db.outbox
        .where({ entity: change.entity, id: change.id })
        .count();
      if (pending > 0) continue;

      const local = await table.get(change.id);
      const localVersion = Number((local as { version?: unknown } | undefined)?.version);
      if (local && Number.isFinite(localVersion) && localVersion >= Number(change.version)) continue;

      if (change.op === "delete") {
        await table.delete(change.id);
      } else if (change.row) {
        await table.put(normalizeServerRow(change.row) as never);
      }
    }
  }

  private async wipeLocalKeepOutbox(db: HaulSheetDB): Promise<void> {
    await db.transaction("rw", [...ENTITY_TABLES.map((t) => db.table(t)), db.meta], async () => {
      for (const name of ENTITY_TABLES) {
        await db.table(name).clear();
      }
      await db.meta.delete("syncCursor");
    });
  }
}

/**
 * Fold unsent patches for one entity+id in creation order, newest last.
 * A pending delete resets the chain (fields before it are gone).
 */
export function mergePendingPatches(
  pending: ReadonlyArray<Pick<OutboxRow, "op" | "patch" | "createdAt">>,
  next: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const ordered = [...pending].sort((x, y) => x.createdAt.localeCompare(y.createdAt));
  let acc: Record<string, unknown> | null = null;
  for (const p of ordered) {
    if (p.op === "delete") {
      acc = null;
      continue;
    }
    if (p.patch) acc = { ...(acc ?? {}), ...p.patch };
  }
  if (!next) return acc;
  return { ...(acc ?? {}), ...next };
}

/**
 * A load status change behind a queued (unsent, non-create) status change is
 * kept as its own mutation instead of being coalesced into it.
 */
export function isSeparateStatusStep(
  args: { entity: string; op: string; patch?: Record<string, unknown> | null },
  pending: ReadonlyArray<Pick<OutboxRow, "op" | "patch" | "baseVersion">>,
): boolean {
  if (args.entity !== "load" || args.op !== "upsert" || !args.patch || !("status" in args.patch)) return false;
  if (pending.length === 0 || pending.some((p) => p.baseVersion == null || p.op !== "upsert")) return false;
  return pending.some((p) => p.patch != null && "status" in p.patch);
}

/** Dead-letter reason for a change parked behind its record's rejected create. */
export const HELD_REASON = "held_behind_failed_create";

export class OfflineError extends Error {
  constructor() {
    super("You're offline. Connect to undo this change.");
    this.name = "OfflineError";
  }
}

let singleton: SyncClient | null = null;

export function getSyncClient(): SyncClient {
  if (!singleton) singleton = new SyncClient();
  return singleton;
}

export function resetSyncClient(): void {
  if (singleton) singleton.stop();
  singleton = null;
}
